import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, Typography } from '@mui/material';
import { getDeviceConsumptionLast24h } from "../../api_calls/devices";
import Alert from '@mui/material/Alert';
import MetabuildDeviceChart from './MetabuildDeviceChart';

const DeviceCardChart = ({ device }) => {
    const [data, setData] = useState({ labels: [], consumption: [] });
    const [error, setError] = useState(null);

    useEffect(() => {
        if (device?.isDemoDevice || !device?.device_id || device.relay === undefined) return;

        getDeviceConsumptionLast24h(device.device_id, device.relay)
            .then(res => {
                const { labels, consumption } = res.data || {};
                if (labels && consumption) {
                    setData({ labels, consumption });
                    setError(null);
                }
            })
            .catch(err => {
                console.error('Chart fetch error:', err);
                setError('Failed to load consumption data. Please try again later.');
            });
    }, [device]);

    // Demo devices (Metabuild) - fetch and display real data
    if (device?.isDemoDevice) {
        return <MetabuildDeviceChart device={device} />;
    }

    if (error) {
        return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
    }
    if (!data.labels.length || !data.consumption.length) {
        return <Box sx={{ height: 80 }} />;
    }

    const total = data.consumption.reduce((sum, val) => sum + val, 0).toFixed(2);

    const option = {
        xAxis: {
            type: 'category',
            data: data.labels,
            axisLine: { show: true },
            axisLabel: {
                color: '#999',
                fontSize: 10,
            },
            axisTick: { show: false },
        },
        yAxis: {
            type: 'value',
            axisLine: { show: false },
            axisLabel: {
                color: '#999',
                fontSize: 10,
            },
            splitLine: {
                lineStyle: {
                    type: 'dashed',
                    color: '#eee',
                },
            },
        },
        series: [
            {
                data: data.consumption,
                type: 'line',
                smooth: true,
                areaStyle: {
                    color: '#2196f3',
                    opacity: 0.3,
                },
                lineStyle: {
                    color: '#2196f3',
                    width: 2,
                },
                symbol: 'none',
            },
        ],
        grid: {
            top: 30,
            bottom: 35,
            left: 35,
            right: 10,
        },
        tooltip: {
            trigger: 'axis',
            formatter: '{b} <br /> {c} kWh',
        },
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                    Last 24 Hours Consumption (kWh)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ color: '#2196f3', mr: 1 }}>
                        Total:
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" fontWeight="bold" color="#2196f3" sx={{ lineHeight: 1 }}>
                            {total}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#2196f3' }}>
                            kWh
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <ReactECharts option={option} style={{ height: 150, width: '100%' }} />
        </Box>
    );
};

export default DeviceCardChart;
